/**
 * CRUD + bulk-операции над товарами заявки.
 *
 * Индивидуальные:
 *   saveProduct(productId)     — POST /manager/request/save-product
 *   approveProduct(productId)  — POST /manager/request/approve-product (в клиентскую спеку)
 *   deleteProduct(productId)   — POST /manager/request/delete-product
 *
 * Массовые:
 *   saveAllProducts()          — POST /manager/save/calculation
 *   approveAllProducts()       — POST /manager/approve/calculation (только выбранные чекбоксами)
 *   getSelectedProducts()      — массив {id, brandId} для bulk
 *   performBulkAction(action)  — 'delete' | 'save' | 'approve' по выбранным
 */

import { apiRequest, APIError } from '../../api.js';
import { collectProductData, updateGrandTotals } from './calculator.js';
import { showConfirmModal } from './confirm-modal.js';

function updatePriceAndTotal(productId, pricePerItem, qty) {
    if (!pricePerItem) return;
    const priceEl = document.getElementById('price_' + productId);
    if (priceEl) priceEl.textContent = parseFloat(pricePerItem).toFixed(2);

    const totalAmount = pricePerItem * (parseFloat(qty) || 1);
    const totalEl = document.getElementById('total_' + productId);
    if (totalEl) totalEl.textContent = totalAmount.toFixed(2);
}

function productEl(productId) {
    return document.querySelector(`.adm_req_brand_item_info[data-product-id="${productId}"]`);
}

export async function saveProduct(productId) {
    const data = collectProductData(productId);

    try {
        await apiRequest('/manager/request/save-product', {
            method: 'POST',
            body: data,
        });
        window.showToast('success', TOAST_MESSAGES.PRODUCT.SAVED);
        updatePriceAndTotal(data.product_id, data.final_price_per_unit, data.product_qty);
    } catch (e) {
        console.error('Error saving product:', e);
        const msg = e instanceof APIError ? e.message : 'Unknown error';
        window.showToast('error', TOAST_MESSAGES.PRODUCT.ERROR_SAVING + ': ' + msg);
    }
}

export async function approveProduct(productId) {
    const data = collectProductData(productId);
    const captionWrap = document.querySelector('.adm_req_caption_wrap');
    data.project_id = captionWrap?.dataset.projectId || null;

    try {
        await apiRequest('/manager/request/approve-product', {
            method: 'POST',
            body: data,
        });
        window.showToast('success', TOAST_MESSAGES.PRODUCT.SENT_TO_CLIENT);
        const statusP = productEl(productId)?.querySelector('.td_status p');
        if (statusP) statusP.textContent = 'Calculated';
    } catch (e) {
        console.error('Error approving product:', e);
        const msg = e instanceof APIError ? e.message : 'Unknown error';
        window.showToast('error', TOAST_MESSAGES.PRODUCT.ERROR_APPROVING + ': ' + msg);
    }
}

export async function deleteProduct(productId) {
    try {
        await apiRequest('/manager/request/delete-product', {
            method: 'POST',
            body: { product_id: productId },
        });

        // Удаляем DOM-элемент товара + чистим пустой бренд/редиректим
        const element = productEl(productId);
        if (element) {
            const brandItem = element.closest('.adm_req_brand_item');
            element.remove();

            if (brandItem) {
                const brandItems = brandItem.querySelector('.adm_req_brand_items');
                if (brandItems && brandItems.querySelectorAll('.adm_req_brand_item_info').length === 0) {
                    brandItem.remove();
                }
            }
        }

        if (document.querySelectorAll('.adm_req_brand_item_info').length === 0) {
            window.location.href = '/manager/requests';
            return;
        }

        updateGrandTotals();
    } catch (e) {
        console.error('Error deleting product:', e);
        const msg = e instanceof APIError ? e.message : 'Unknown error';
        window.showToast('error', TOAST_MESSAGES.PRODUCT.ERROR_DELETING + ': ' + msg);
    }
}

export async function saveAllProducts() {
    const allData = [];
    document.querySelectorAll('.adm_req_brand_item_info').forEach(el => {
        allData.push(collectProductData(el.dataset.productId));
    });

    try {
        await apiRequest('/manager/save/calculation', {
            method: 'POST',
            body: { products: allData },
        });
        window.showToast('success', TOAST_MESSAGES.PRODUCT.ALL_SAVED);
        allData.forEach(p => updatePriceAndTotal(p.product_id, p.final_price_per_unit, p.product_qty));
    } catch (e) {
        console.error('Error saving products:', e);
        const msg = e instanceof APIError ? e.message : 'Unknown error';
        window.showToast('error', 'Error saving products: ' + msg);
    }
}

export async function approveAllProducts() {
    const allData = [];
    document.querySelectorAll('.product-checkbox:checked').forEach(cb => {
        const data = collectProductData(cb.value);
        data.approve = true;
        allData.push(data);
    });

    if (allData.length === 0) {
        window.showToast('error', TOAST_MESSAGES.PRODUCT.SELECT_FOR_APPROVAL);
        return;
    }

    try {
        await apiRequest('/manager/approve/calculation', {
            method: 'POST',
            body: { products: allData },
        });
        const message = allData.length === 1
            ? '1 product sent to client successfully'
            : `${allData.length} products sent to client successfully`;
        window.showToast('success', message);
        document.querySelectorAll('.td_status p').forEach(p => { p.textContent = 'Calculated'; });
    } catch (e) {
        const msg = e instanceof APIError ? e.message : e.message || 'Unknown error';
        window.showToast('error', 'Error approving products: ' + msg);
    }
}

export function getSelectedProducts() {
    const selected = [];
    document.querySelectorAll('.product-checkbox:checked').forEach(cb => {
        selected.push({
            id: cb.value,
            brandId: cb.closest('.adm_req_brand_item')?.dataset.brandId || null,
        });
    });
    return selected;
}

export function performBulkAction(action) {
    const selected = getSelectedProducts();
    if (selected.length === 0) {
        window.showToast('warning', 'Please select at least one product');
        return;
    }

    if (action === 'delete') {
        showConfirmModal(
            `Delete ${selected.length} selected products?`,
            () => { selected.forEach(item => deleteProduct(item.id)); }
        );
        return;
    }

    if (action === 'save') {
        const allData = selected.map(item => collectProductData(item.id));
        (async () => {
            try {
                await apiRequest('/manager/request/save-selected', {
                    method: 'POST',
                    body: { products: allData },
                });
                window.showToast('success', TOAST_MESSAGES.PRODUCT.SELECTED_SAVED);
                allData.forEach(p => updatePriceAndTotal(p.product_id, p.final_price_per_unit, p.product_qty));
            } catch (e) {
                const msg = e instanceof APIError ? e.message : 'Unknown error';
                window.showToast('error', 'Error saving selected products: ' + msg);
            }
        })();
        return;
    }

    if (action === 'approve') {
        showConfirmModal(
            `Send ${selected.length} selected products to client?`,
            async () => {
                const allData = selected.map(item => {
                    const data = collectProductData(item.id);
                    data.approve = true;
                    return data;
                });

                try {
                    await apiRequest('/manager/request/approve-selected', {
                        method: 'POST',
                        body: { products: allData },
                    });
                    window.showToast('success', TOAST_MESSAGES.PRODUCT.SENT_TO_CLIENT);
                    selected.forEach(item => {
                        const statusP = productEl(item.id)?.querySelector('.td_status p');
                        if (statusP) statusP.textContent = 'Calculated';
                    });
                } catch (e) {
                    const msg = e instanceof APIError ? e.message : 'Unknown error';
                    window.showToast('error', 'Error approving selected products: ' + msg);
                }
            }
        );
    }
}
